import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { css, cx } from "styled-system/css";
import { Check, X, ChevronDown } from "lucide-react";
import { api, type ApiResponse } from "~/lib/api";
import { ak, Modal, PageHead, Section } from "~/components/editorial";
import type { Tool, Label, AccessRole } from "./types";
import { AUTH_METHODS, AUTH_METHOD_LABELS } from "./types";

interface JiraProject {
  id: string;
  key: string;
  name: string;
  avatarUrl: string | null;
}

// Reusable multiselect dropdown
function MultiSelect({
  options,
  selectedIds,
  onToggle,
  placeholder,
  renderChip,
  renderOption,
  footer,
  searchable,
}: {
  options: { id: string; label: string }[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  placeholder: string;
  renderChip?: (id: string) => React.ReactNode;
  renderOption?: (id: string) => React.ReactNode;
  footer?: React.ReactNode;
  searchable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const visibleOptions =
    searchable && search
      ? options.filter((o) =>
          o.label.toLowerCase().includes(search.toLowerCase()),
        )
      : options;

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div className={multiStyles.control} onClick={() => setOpen((v) => !v)}>
        {selectedIds.length === 0 ? (
          <span className={multiStyles.placeholder}>{placeholder}</span>
        ) : (
          <div className={multiStyles.chipRow}>
            {selectedIds.map((id) => (
              <span key={id} className={multiStyles.chip}>
                {renderChip
                  ? renderChip(id)
                  : options.find((o) => o.id === id)?.label ?? id}
                <button
                  type="button"
                  className={multiStyles.chipRemove}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(id);
                  }}
                >
                  <X size={10} />
                </button>
              </span>
            ))}
          </div>
        )}
        <ChevronDown size={14} className={multiStyles.chevron} />
      </div>

      {open && (
        <div className={multiStyles.dropdown}>
          {searchable && (
            <div className={multiStyles.searchRow}>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className={multiStyles.searchInput}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}
          {visibleOptions.length === 0 && !footer && (
            <div className={multiStyles.emptyOption}>
              {search ? "No matches" : "No options available"}
            </div>
          )}
          {visibleOptions.map((opt) => {
            const selected = selectedIds.includes(opt.id);
            return (
              <button
                key={opt.id}
                type="button"
                className={multiStyles.option}
                onClick={() => onToggle(opt.id)}
              >
                <span className={multiStyles.optionCheck}>
                  {selected && <Check size={12} />}
                </span>
                {renderOption ? renderOption(opt.id) : opt.label}
              </button>
            );
          })}
          {footer}
        </div>
      )}
    </div>
  );
}

export function ToolFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [authMethod, setAuthMethod] = useState("");
  const [authMethodOther, setAuthMethodOther] = useState("");
  const [accessProvider, setAccessProvider] = useState("");
  const [notes, setNotes] = useState("");
  const [isPaid, setIsPaid] = useState(false);
  const [renewalDate, setRenewalDate] = useState("");
  const [renewalNotes, setRenewalNotes] = useState("");
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([]);
  const [selectedJiraProjectIds, setSelectedJiraProjectIds] = useState<
    string[]
  >([]);
  const [showTeamsModal, setShowTeamsModal] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: labelsData } = useQuery({
    queryKey: ["labels"],
    queryFn: () => api.get<ApiResponse<Label[]>>("/labels"),
  });
  const labels = labelsData?.data ?? [];

  const { data: rolesData } = useQuery({
    queryKey: ["access-roles"],
    queryFn: () => api.get<ApiResponse<AccessRole[]>>("/access-roles"),
  });
  const roles = rolesData?.data ?? [];

  const { data: jiraProjectsData } = useQuery({
    queryKey: ["jira-projects-dropdown"],
    queryFn: () => api.get<ApiResponse<JiraProject[]>>("/jira/projects"),
  });
  const jiraProjects = jiraProjectsData?.data ?? [];

  const { data: toolData } = useQuery({
    queryKey: ["tool", id],
    queryFn: () => api.get<ApiResponse<Tool>>(`/tools/${id}`),
    enabled: isEdit,
  });

  // Seed the form when the tool loads (and again if the server copy changes)
  const [seededTool, setSeededTool] = useState<Tool | null>(null);
  const tool = toolData?.data;
  if (tool && tool !== seededTool) {
    setSeededTool(tool);
    setName(tool.name);
    setUrl(tool.url ?? "");
    setDescription(tool.description ?? "");
    setAccessProvider(tool.accessProvider ?? "");
    setNotes(tool.notes ?? "");
    setIsPaid(tool.isPaid);
    setRenewalDate(tool.renewalDate ? tool.renewalDate.slice(0, 10) : "");
    setRenewalNotes(tool.renewalNotes ?? "");
    setSelectedLabelIds(tool.labels.map((l) => l.labelId));
    setSelectedRoleIds(tool.accessRoles.map((r) => r.accessRoleId));
    setSelectedJiraProjectIds(tool.jiraTeams.map((jt) => jt.jiraProjectId));
    if (tool.authMethod) {
      if (AUTH_METHODS.includes(tool.authMethod)) {
        setAuthMethod(tool.authMethod);
      } else {
        setAuthMethod("other");
        setAuthMethodOther(tool.authMethod);
      }
    }
  }

  const toggleLabel = (labelId: string) => {
    setSelectedLabelIds((prev) =>
      prev.includes(labelId)
        ? prev.filter((i) => i !== labelId)
        : [...prev, labelId],
    );
  };

  const toggleRole = (roleId: string) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((i) => i !== roleId)
        : [...prev, roleId],
    );
  };

  const toggleJiraProject = (projId: string) => {
    setSelectedJiraProjectIds((prev) =>
      prev.includes(projId)
        ? prev.filter((i) => i !== projId)
        : [...prev, projId],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);

    const resolvedAuthMethod =
      authMethod === "other"
        ? authMethodOther.trim() || "other"
        : authMethod || undefined;

    const payload = {
      name: name.trim(),
      url: url.trim() || undefined,
      description: description.trim() || undefined,
      authMethod: resolvedAuthMethod,
      accessProvider: accessProvider.trim() || undefined,
      notes: notes.trim() || undefined,
      isPaid,
      renewalDate:
        isPaid && renewalDate ? new Date(renewalDate).toISOString() : null,
      renewalNotes:
        isPaid && renewalNotes.trim() ? renewalNotes.trim() : undefined,
      labelIds: selectedLabelIds,
      accessRoleIds: selectedRoleIds,
      jiraProjectIds: selectedJiraProjectIds,
    };

    try {
      if (isEdit && id) {
        await api.patch(`/tools/${id}`, payload);
        queryClient.invalidateQueries({ queryKey: ["tool", id] });
        navigate(`/tools/${id}`);
      } else {
        const res = await api.post<ApiResponse<Tool>>("/tools", payload);
        queryClient.invalidateQueries({ queryKey: ["tools"] });
        navigate(`/tools/${res.data.id}`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectOptions = jiraProjects.map((p) => ({ id: p.id, label: p.name }));

  return (
    <div className={ak.page}>
      <PageHead
        back={
          isEdit && id
            ? { to: `/tools/${id}`, label: "Back to tool" }
            : { to: "/tools", label: "Tools" }
        }
        kicker="Admin · Tools tracker"
        title={isEdit ? `Edit ${name || "tool"}` : "Add a tool"}
        dek="Starred fields are required. Everything else can be filled in later."
      />

      <form onSubmit={handleSubmit}>
        <Section kicker="I. The tool">
          <div className={ak.field}>
            <label className={ak.label} htmlFor="tool-name">
              Name *
            </label>
            <input
              id="tool-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Confluence, GitHub, Figma…"
              className={ak.input}
              required
              autoFocus
            />
          </div>

          <div className={ak.field}>
            <label className={ak.label} htmlFor="tool-url">
              Base tool URL
            </label>
            <input
              id="tool-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://atlassian.net"
              className={cx(ak.input, ak.mono)}
            />
            <p className={ak.hint}>
              The main website or login page. Team links, like a team's
              Confluence space, are added per team once the tool exists.
            </p>
          </div>

          <div className={ak.field}>
            <label className={ak.label} htmlFor="tool-description">
              Description
            </label>
            <textarea
              id="tool-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this tool used for?"
              className={ak.textarea}
              rows={3}
            />
          </div>

          <div className={ak.field}>
            <label className={ak.label}>Labels</label>
            <MultiSelect
              options={labels.map((l) => ({ id: l.id, label: l.name }))}
              selectedIds={selectedLabelIds}
              onToggle={toggleLabel}
              placeholder="Add labels…"
              renderChip={(id) => {
                const l = labels.find((x) => x.id === id);
                return <LabelName name={l?.name ?? id} color={l?.color} />;
              }}
              renderOption={(id) => {
                const l = labels.find((x) => x.id === id);
                return <LabelName name={l?.name ?? id} color={l?.color} />;
              }}
            />
          </div>
        </Section>

        <Section kicker="II. Access">
          <div className={ak.field}>
            <label className={ak.label} htmlFor="tool-auth">
              Auth method
            </label>
            <select
              id="tool-auth"
              value={authMethod}
              onChange={(e) => setAuthMethod(e.target.value)}
              className={ak.select}
            >
              <option value="">Select…</option>
              {AUTH_METHODS.map((m) => (
                <option key={m} value={m}>
                  {AUTH_METHOD_LABELS[m]}
                </option>
              ))}
            </select>
            {authMethod === "other" && (
              <input
                type="text"
                value={authMethodOther}
                onChange={(e) => setAuthMethodOther(e.target.value)}
                placeholder="Describe the auth method…"
                aria-label="Other auth method"
                className={ak.input}
              />
            )}
          </div>

          <div className={ak.field}>
            <label className={ak.label} htmlFor="tool-access">
              Getting access
            </label>
            <textarea
              id="tool-access"
              value={accessProvider}
              onChange={(e) => setAccessProvider(e.target.value)}
              placeholder="Links and steps for getting access…"
              className={ak.textarea}
              rows={3}
            />
          </div>

          <div className={ak.field}>
            <label className={ak.label}>Who should have access</label>
            <MultiSelect
              options={roles.map((r) => ({ id: r.id, label: r.name }))}
              selectedIds={selectedRoleIds}
              onToggle={toggleRole}
              placeholder="Add roles…"
            />
          </div>

          <div className={ak.field}>
            <label className={ak.label}>Teams</label>
            {selectedJiraProjectIds.length > 0 && (
              <div className={ak.tagRow}>
                {selectedJiraProjectIds.map((projId) => {
                  const p = jiraProjects.find((x) => x.id === projId);
                  return (
                    <span key={projId} className={ak.tag}>
                      {p ? p.name : projId}
                      {p && (
                        <span className={cx(ak.mono, ak.muted)}>{p.key}</span>
                      )}
                      <button
                        type="button"
                        className={multiStyles.chipRemove}
                        onClick={() => toggleJiraProject(projId)}
                        aria-label={`Remove ${p?.name ?? projId}`}
                      >
                        <X size={10} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <div>
              <button
                type="button"
                onClick={() => setShowTeamsModal(true)}
                className={cx(ak.btnSecondary, ak.btnSm)}
              >
                {selectedJiraProjectIds.length > 0
                  ? "Change teams"
                  : "Add teams"}
              </button>
            </div>
            <p className={ak.hint}>
              Teams are Jira projects that use the tool.
            </p>
          </div>
        </Section>

        <Section kicker="III. Billing">
          <div className={ak.field}>
            <label className={ak.checkRow}>
              <input
                type="checkbox"
                checked={isPaid}
                onChange={(e) => setIsPaid(e.target.checked)}
              />
              Paid tool
            </label>
          </div>

          {isPaid && (
            <>
              <div className={ak.field}>
                <label className={ak.label} htmlFor="tool-renewal">
                  Renewal date
                </label>
                <input
                  id="tool-renewal"
                  type="date"
                  value={renewalDate}
                  onChange={(e) => setRenewalDate(e.target.value)}
                  className={cx(ak.input, ak.mono, styles.date)}
                />
              </div>
              <div className={ak.field}>
                <label className={ak.label} htmlFor="tool-renewal-notes">
                  Renewal notes
                </label>
                <textarea
                  id="tool-renewal-notes"
                  value={renewalNotes}
                  onChange={(e) => setRenewalNotes(e.target.value)}
                  placeholder="Who to contact, whether it auto-renews…"
                  className={ak.textarea}
                  rows={2}
                />
              </div>
            </>
          )}

          <div className={ak.field}>
            <label className={ak.label} htmlFor="tool-notes">
              Notes
            </label>
            <textarea
              id="tool-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else worth knowing…"
              className={ak.textarea}
              rows={3}
            />
          </div>

          <div className={ak.formActions}>
            <button
              type="submit"
              disabled={!name.trim() || isSubmitting}
              className={ak.btn}
            >
              {isSubmitting
                ? isEdit
                  ? "Saving…"
                  : "Creating…"
                : isEdit
                  ? "Save changes"
                  : "Create tool"}
            </button>
            <Link
              to={isEdit && id ? `/tools/${id}` : "/tools"}
              className={ak.btnSecondary}
            >
              Cancel
            </Link>
          </div>
        </Section>
      </form>

      {/* Teams modal */}
      {showTeamsModal && (
        <Modal title="Teams" onClose={() => setShowTeamsModal(false)}>
          <div className={ak.field}>
            <label className={ak.label}>Teams that use this tool</label>
            <MultiSelect
              options={projectOptions}
              selectedIds={selectedJiraProjectIds}
              onToggle={toggleJiraProject}
              placeholder="Select teams…"
              searchable
              renderChip={(projId) => {
                const p = jiraProjects.find((x) => x.id === projId);
                return <span>{p ? p.name : projId}</span>;
              }}
              renderOption={(projId) => {
                const p = jiraProjects.find((x) => x.id === projId);
                return (
                  <span className={multiStyles.projectOption}>
                    {p?.name}
                    <span className={multiStyles.projectKey}>{p?.key}</span>
                  </span>
                );
              }}
            />
          </div>
          <div className={ak.modalActions}>
            <button
              type="button"
              onClick={() => setShowTeamsModal(false)}
              className={ak.btn}
            >
              Done
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LabelName({ name, color }: { name: string; color?: string | null }) {
  return (
    <span className={multiStyles.labelName}>
      {color && (
        <span
          className={ak.swatch}
          style={{ backgroundColor: color }}
          aria-hidden
        />
      )}
      {name}
    </span>
  );
}

const multiStyles = {
  control: css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "36px",
    px: "10px",
    py: "5px",
    fontSize: "14px",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: "0",
    bg: "ed.paper",
    color: "ed.ink",
    cursor: "pointer",
    _hover: { borderColor: "ed.ink3" },
    _focusVisible: {
      outline: "1px solid",
      outlineColor: "ed.ink",
      borderColor: "ed.ink",
    },
  }),
  placeholder: css({ color: "ed.ink3", fontSize: "14px" }),
  chipRow: css({ display: "flex", flexWrap: "wrap", gap: "4px", flex: 1 }),
  chip: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    px: "6px",
    py: "1px",
    fontSize: "12px",
    fontWeight: 600,
    color: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink3",
    borderRadius: "0",
  }),
  chipRemove: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    bg: "transparent",
    border: "none",
    cursor: "pointer",
    color: "ed.ink3",
    p: 0,
    _hover: { color: "ed.ink" },
  }),
  chevron: css({ color: "ed.ink3", flexShrink: 0, ml: "8px" }),
  // Opaque paper, never transparent.
  dropdown: css({
    position: "absolute",
    top: "calc(100% - 1px)",
    left: 0,
    right: 0,
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: "0",
    zIndex: 100,
    maxHeight: "260px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    bg: "ed.paper",
  }),
  searchRow: css({
    p: "6px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.rule",
    flexShrink: 0,
  }),
  searchInput: css({
    width: "100%",
    px: "8px",
    py: "4px",
    fontSize: "13px",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: "0",
    bg: "ed.paper",
    color: "ed.ink",
    outline: "none",
    _focus: { borderColor: "ed.ink" },
    _placeholder: { color: "ed.ink3" },
  }),
  option: css({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    px: "10px",
    py: "6px",
    fontSize: "13.5px",
    textAlign: "left",
    bg: "transparent",
    border: "none",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.grid",
    cursor: "pointer",
    color: "ed.ink",
    _hover: { bg: "ed.panel" },
  }),
  optionCheck: css({
    width: "14px",
    height: "14px",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid",
    borderColor: "ed.ink3",
    color: "ed.ink",
  }),
  emptyOption: css({
    px: "10px",
    py: "8px",
    fontSize: "13px",
    color: "ed.ink3",
    fontStyle: "italic",
  }),
  projectOption: css({
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    flex: 1,
  }),
  projectKey: css({
    ml: "auto",
    fontFamily: "mono",
    fontSize: "11.5px",
    color: "ed.ink3",
  }),
  labelName: css({ display: "inline-flex", alignItems: "center", gap: "5px" }),
};

const styles = {
  date: css({ maxWidth: "200px" }),
};
